/**
 * admin_database_predeploy_controller.js — Admin > Database > Pre-Deploy Check (Stimulus).
 *
 * Connects to production, then checks public and tenant schema alignment and
 * pending migrations, drawing each result into its card. connect() starts the
 * checks, so they run on every visit: the inline <script> this replaces waited
 * for DOMContentLoaded, which a Turbo visit never fires, and left the page on
 * its loading spinners until a hard reload.
 *
 * The card refresh buttons call #checkPublic / #checkTenant / #checkMigrations;
 * a drawn result's Copy button calls #copySql with a
 * data-admin-database-predeploy-target-param naming the <pre> to copy.
 *
 * Template: templates/admin/database/predeploy.html
 */

import { Controller } from "@hotwired/stimulus";

// Atlas writes a "-- <Verb> ..." comment before each statement; these name the
// statement's object. First rule whose prefix matches decides (else-if order).
const HEADER_RULES = [
    ['-- Modify "', /-- Modify "(\w+)" table/, 'bi-pencil', 'warning', 'Modify table: ', 'medium'],
    ['-- Create index', /-- Create index "(\w+)"/, 'bi-plus-circle', 'info', 'Add index: ', 'low'],
    ['-- Drop index', /-- Drop index "(\w+)"/, 'bi-dash-circle', 'warning', 'Remove index: ', 'low'],
    ['-- Create "', /-- Create "(\w+)" table/, 'bi-plus-square', 'success', 'Create table: ', 'high'],
    ['-- Drop "', /-- Drop "(\w+)" table/, 'bi-trash', 'danger', 'Drop table: ', 'critical'],
];

// Clauses inside a statement. First rule whose keyword the line contains decides.
const CLAUSE_RULES = [
    ['ADD COLUMN', /ADD COLUMN "(\w+)"/, 'bi-plus', 'success', 'Add column: ', 'medium'],
    ['DROP COLUMN', /DROP COLUMN "(\w+)"/, 'bi-dash', 'danger', 'Remove column: ', 'high'],
    ['SET DEFAULT', /"(\w+)" SET DEFAULT/, 'bi-gear', 'secondary', 'Set default on: ', 'low'],
    ['DROP DEFAULT', /"(\w+)" DROP DEFAULT/, 'bi-gear', 'secondary', 'Remove default on: ', 'low'],
    ['SET NOT NULL', /"(\w+)" SET NOT NULL/, 'bi-shield', 'warning', 'Add NOT NULL: ', 'medium'],
    ['DROP NOT NULL', /"(\w+)" DROP NOT NULL/, 'bi-shield', 'info', 'Remove NOT NULL: ', 'low'],
    ['ADD CONSTRAINT', /ADD CONSTRAINT "(\w+)"/, 'bi-link', 'info', 'Add constraint: ', 'low'],
    ['DROP CONSTRAINT', /DROP CONSTRAINT "(\w+)"/, 'bi-link-45deg', 'warning', 'Remove constraint: ', 'medium'],
];

const CHANGE_COMMENTS = ['-- Modify', '-- Create', '-- Drop', '-- Add'];

const SEVERITIES = {
    critical: { label: 'Critical Changes (Data Loss Risk)', color: 'danger', icon: 'bi-exclamation-octagon' },
    high: { label: 'High Impact Changes', color: 'warning', icon: 'bi-exclamation-triangle' },
    medium: { label: 'Medium Impact Changes', color: 'info', icon: 'bi-info-circle' },
    low: { label: 'Low Impact Changes (Safe)', color: 'success', icon: 'bi-check-circle' },
};

const HEADER_CLASS = ' text-white d-flex justify-content-between align-items-center';

function byId(id) { return document.getElementById(id); }

function show(id, visible) { byId(id).style.display = visible ? '' : 'none'; }

function startsWith(line, prefix) { return line.indexOf(prefix) === 0; }

function contains(line, keyword) { return line.indexOf(keyword) !== -1; }

/** The change the first matching rule reports for `line`, or null. */
function firstChange(line, rules, test) {
    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        if (!test(line, rule[0])) continue;
        const match = line.match(rule[1]);
        return match ? { icon: rule[2], color: rule[3], text: rule[4] + match[1], severity: rule[5] } : null;
    }
    return null;
}

function isOtherComment(line) {
    return startsWith(line, '--') && !CHANGE_COMMENTS.some(function (c) { return startsWith(line, c); });
}

/** The schema changes an Atlas diff makes, one per object, in diff order. */
function parseDiffSql(sql) {
    const changes = [];
    sql.split('\n').forEach(function (raw) {
        const line = raw.trim();
        if (isOtherComment(line)) return;
        [firstChange(line, HEADER_RULES, startsWith), firstChange(line, CLAUSE_RULES, contains)]
            .forEach(function (change) { if (change) changes.push(change); });
    });
    const seen = {};
    return changes.filter(function (c) {
        if (seen[c.text]) return false;
        seen[c.text] = true;
        return true;
    });
}

/** Changes grouped by severity, worst first, empty groups left out. */
function categorize(changes) {
    return ['critical', 'high', 'medium', 'low'].map(function (severity) {
        const items = changes.filter(function (c) { return c.severity === severity; });
        return Object.assign({ severity: severity, items: items }, SEVERITIES[severity]);
    }).filter(function (cat) { return cat.items.length > 0; });
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function misalignmentAlert(categories) {
    const has = function (s) { return categories.some(function (c) { return c.severity === s; }); };
    const critical = has('critical');
    const title = critical ? 'Critical Misalignment!' : (has('high') ? 'Significant Misalignment!' : 'Schema Misalignment');
    return '<div class="alert alert-' + (critical ? 'danger' : 'warning') + ' mb-3"><i class="bi bi-exclamation-triangle me-2"></i><strong>'
        + title + '</strong> Production schema differs from local models.</div>';
}

function categoriesHtml(categories) {
    if (categories.length === 0) return '';
    return '<div class="mb-3">' + categories.map(function (cat) {
        return '<div class="mb-2"><h6 class="d-flex align-items-center text-' + cat.color + ' mb-1">'
            + '<i class="bi ' + cat.icon + ' me-2"></i>' + cat.label
            + '<span class="badge bg-' + cat.color + ' ms-2">' + cat.items.length + '</span></h6>'
            + '<ul class="list-group list-group-flush small">' + cat.items.map(function (c) {
                return '<li class="list-group-item py-1 d-flex align-items-center"><i class="bi ' + c.icon
                    + ' text-' + c.color + ' me-2"></i>' + c.text + '</li>';
            }).join('') + '</ul></div>';
    }).join('') + '</div>';
}

function sqlHtml(sql, containerId) {
    if (!sql) return '';
    const preId = containerId + 'Sql';
    return '<div class="mb-3"><div class="d-flex justify-content-between align-items-center mb-2">'
        + '<h6 class="mb-0"><i class="bi bi-code-slash me-2"></i>SQL to Align Production with Models</h6>'
        + '<button type="button" class="btn btn-sm btn-outline-secondary" data-action="admin-database-predeploy#copySql"'
        + ' data-admin-database-predeploy-target-param="' + preId + '"><i class="bi bi-clipboard me-1"></i>Copy</button></div>'
        + '<pre id="' + preId + '" class="p-3 bg-dark text-light rounded" style="max-height: 300px; overflow-y: auto;">'
        + escapeHtml(sql) + '</pre></div>';
}

function alignmentHtml(data, containerId) {
    if (data.error) return '<div class="alert alert-danger"><i class="bi bi-x-circle me-2"></i>' + data.error + '</div>';
    if (data.aligned) {
        return '<div class="alert alert-success mb-0"><i class="bi bi-check-circle me-2"></i><strong>Aligned!</strong>'
            + ' Production schema matches local models - ready to deploy.</div>';
    }
    const categories = categorize(parseDiffSql(data.diff_sql || ''));
    return misalignmentAlert(categories) + categoriesHtml(categories) + sqlHtml(data.diff_sql, containerId);
}

function statusCard(icon, color, title, message) {
    return '<div class="card border-' + color + '"><div class="card-body text-center py-5">'
        + '<i class="bi ' + icon + ' fs-1 text-' + color + ' mb-3"></i><h4>' + title + '</h4>'
        + '<p class="text-muted">' + message + '</p></div></div>';
}

function pendingHtml(data, tenantCount) {
    if (data.error) return '<div class="alert alert-danger small">' + data.error + '</div>';
    if (!(data.pending_count > 0)) {
        return '<div class="alert alert-success small mb-0"><i class="bi bi-check-circle me-1"></i>Up to date</div>';
    }
    const scale = tenantCount ? ' per tenant (' + (data.pending_count * tenantCount) + ' total)' : '';
    return '<div class="alert alert-warning small"><strong>' + data.pending_count + ' pending</strong>' + scale + '</div>'
        + '<ul class="list-group list-group-flush small">' + (data.pending_files || []).map(function (f) {
            return '<li class="list-group-item py-1"><code>' + f.filename + '</code></li>';
        }).join('') + '</ul>';
}

function getJson(url) {
    return fetch(url).then(function (r) { return r.json(); });
}

export default class extends Controller {
    connect() {
        this._tenantSchema = null;
        this._tenantCount = 0;
        this._checkConnection();
    }

    checkPublic() {
        this._checkAlignment('public', 'publicAlignment');
    }

    checkTenant() {
        if (!this._tenantSchema) {
            show('tenantAlignmentLoading', false);
            show('tenantAlignmentResult', true);
            byId('tenantAlignmentResult').innerHTML = '<div class="alert alert-info">No tenant schemas found on production.</div>';
            return;
        }
        this._checkAlignment(this._tenantSchema, 'tenantAlignment');
    }

    checkMigrations() {
        show('migrationsLoading', true);
        show('migrationsResult', false);
        getJson('/admin/database/api/predeploy/migration-status/public').then(function (data) {
            byId('publicMigrations').innerHTML = pendingHtml(data, 0);
        });
        if (!this._tenantSchema) {
            byId('tenantMigrations').innerHTML = '<span class="text-muted small">No tenants</span>';
            this._migrationsShown();
            return;
        }
        getJson('/admin/database/api/predeploy/migration-status/' + this._tenantSchema).then((data) => {
            byId('tenantMigrations').innerHTML = pendingHtml(data, this._tenantCount);
            this._migrationsShown();
        });
    }

    copySql(event) {
        const el = byId(event.params.target);
        if (!el) return;
        navigator.clipboard.writeText(el.textContent).then(function () {
            alert('SQL copied to clipboard');
        });
    }

    _migrationsShown() {
        show('migrationsLoading', false);
        show('migrationsResult', true);
    }

    _checkConnection() {
        const status = byId('connectionStatus');
        getJson('/admin/database/api/predeploy/status')
            .then((data) => {
                if (!data.configured) {
                    status.innerHTML = statusCard('bi-exclamation-triangle', 'warning', 'Remote Connection Not Configured',
                        'Set <code>REMOTE_DB_PASSWORD</code> environment variable to enable pre-deploy checks.');
                } else if (!data.connected) {
                    status.innerHTML = statusCard('bi-wifi-off', 'danger', 'Cannot Connect to Production', data.error || 'Connection failed');
                } else {
                    this._connected(data);
                }
            })
            .catch(function (err) {
                status.innerHTML = statusCard('bi-x-circle', 'danger', 'Error', err.message);
            });
    }

    _connected(data) {
        byId('connectionStatus').innerHTML = '<div class="alert alert-success mb-4"><i class="bi bi-check-circle me-2"></i>'
            + '<strong>Connected to production</strong> (' + ((data.environment && data.environment.host) || 'oracle') + ')</div>';
        show('mainContent', true);
        const tenant = data.tenant;
        if (tenant && tenant.schemas && tenant.schemas.length > 0) {
            this._tenantSchema = tenant.schemas[0];
            this._tenantCount = tenant.count || tenant.schemas.length;
            byId('tenantCount').textContent = '(' + this._tenantCount + ' tenants)';
        }
        this.checkPublic();
        this.checkTenant();
        this.checkMigrations();
    }

    _checkAlignment(schema, prefix) {
        const result = byId(prefix + 'Result');
        show(prefix + 'Loading', true);
        show(prefix + 'Result', false);
        getJson('/admin/database/api/predeploy/alignment/' + schema)
            .then(function (data) {
                result.innerHTML = alignmentHtml(data, prefix + 'Result');
                byId(prefix + 'Card').querySelector('.card-header').className =
                    'card-header ' + (data.aligned ? 'bg-success' : 'bg-danger') + HEADER_CLASS;
            })
            .catch(function (err) {
                result.innerHTML = '<div class="alert alert-danger">' + err.message + '</div>';
            })
            .finally(function () {
                show(prefix + 'Loading', false);
                show(prefix + 'Result', true);
            });
    }
}
